import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth/AuthContext";

const WedflowMark = () => (
  <a
    href="#top"
    data-testid="brand-wordmark"
    className="flex items-center gap-2 select-none"
    aria-label="WEDFLOW"
  >
    <span
      aria-hidden
      className="inline-block w-1.5 h-1.5 rounded-full"
      style={{ background: "#c9a86a" }}
    />
    <span className="font-display tracking-[0.28em] text-[13px] md:text-sm font-medium text-neutral-900">
      WEDFLOW
    </span>
  </a>
);

const NavSkeleton = () => (
  <span className="inline-block w-20 h-3 rounded-full bg-white/30 animate-pulse" />
);

export default function Navbar() {
  const { t } = useI18n();
  const { user, supabaseUser, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#how", label: t.nav.how, id: "nav-link-how" },
    { href: "#benefits", label: t.nav.benefits, id: "nav-link-benefits" },
    { href: "#pricing", label: t.nav.pricing, id: "nav-link-pricing" },
    { href: "#faq", label: t.nav.faq, id: "nav-link-faq" },
    { href: "/partner", label: "Diventa Partner", id: "nav-link-partner" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "wf-nav-glass" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 h-16 md:h-[72px] flex items-center justify-between">
        <WedflowMark />

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={l.id}
              className="wf-link text-[13px] tracking-wide text-neutral-700 hover:text-neutral-900"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={(user || supabaseUser) ? "/dashboard" : "/login"}
            data-testid="nav-cta-create-wedding"
            className="hidden sm:inline-flex wf-btn-primary rounded-full px-5 py-2.5 text-[13px] tracking-wide items-center"
          >
            {loading && !supabaseUser ? <NavSkeleton /> : (user || supabaseUser) ? "Dashboard" : t.nav.cta}
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
            className="md:hidden w-10 h-10 grid place-items-center rounded-full border border-neutral-200"
          >
            <span className="relative block w-4 h-3">
              <span
                className={`absolute left-0 top-0 w-4 h-px bg-neutral-900 transition-transform ${
                  open ? "translate-y-[6px] rotate-45" : ""
                }`}
              />
              <span
                className={`absolute left-0 top-1/2 w-4 h-px bg-neutral-900 -translate-y-1/2 transition-opacity ${
                  open ? "opacity-0" : "opacity-100"
                }`}
              />
              <span
                className={`absolute left-0 bottom-0 w-4 h-px bg-neutral-900 transition-transform ${
                  open ? "-translate-y-[6px] -rotate-45" : ""
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      <div
        data-testid="nav-mobile-menu"
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
          open ? "max-h-[360px] opacity-100" : "max-h-0 opacity-0"
        } bg-white/90 backdrop-blur-xl border-b border-neutral-200`}
      >
        <nav className="px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <a
              key={l.id + "-mobile"}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={l.id + "-mobile"}
              className="text-neutral-800 text-base tracking-wide"
            >
              {l.label}
            </a>
          ))}
          <a
            href={(user || supabaseUser) ? "/dashboard" : "/login"}
            onClick={() => setOpen(false)}
            data-testid="nav-cta-create-wedding-mobile"
            className="wf-btn-primary rounded-full text-center py-3 mt-2 text-[13px] tracking-wide flex items-center justify-center"
          >
            {loading && !supabaseUser ? <NavSkeleton /> : (user || supabaseUser) ? "Dashboard" : t.nav.cta}
          </a>
        </nav>
      </div>
    </header>
  );
}
